/**
 * Design System - SearchBar Component
 *
 * Main search/chat interface component
 */

import React from 'react';
import { cn } from '../../utils/cn';

export interface SearchBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Value of the input
   */
  value?: string;
  /**
   * On change handler
   */
  onChange?: (value: string) => void;
  /**
   * On submit handler
   */
  onSubmit?: () => void;
  /**
   * Show microphone button
   */
  showMicrophone?: boolean;
  /**
   * Show attachment button
   */
  showAttachment?: boolean;
  /**
   * Disclaimer text
   */
  disclaimerText?: string;
}

export const SearchBar = React.forwardRef<HTMLDivElement, SearchBarProps>(
  (
    {
      className,
      placeholder = 'Ask anything about travel.',
      value,
      onChange,
      onSubmit,
      showMicrophone = true,
      showAttachment = true,
      disclaimerText = 'Nexgo is AI-powered and may not always be accurate. Verify when needed.',
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState(value || '');

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange?.(newValue);
    };

    const handleSubmit = () => {
      if (inputValue.trim()) {
        onSubmit?.();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    return (
      <div
        className={cn('flex flex-col gap-1 w-full', className)}
        ref={ref}
        {...props}
      >
        <div className='bg-white border border-[#D6D3D1] rounded-lg overflow-hidden'>
          <div className='flex flex-col gap-3 p-3'>
            {/* Input Area */}
            <div className='px-2.5 py-2.5'>
              <textarea
                className='w-full resize-none text-sm text-[#1C1917] placeholder:text-[#78716C] focus:outline-none bg-transparent min-h-[24px] max-h-[120px]'
                placeholder={placeholder}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                rows={1}
              />
            </div>

            {/* Action Bar */}
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                {showAttachment && (
                  <button className='h-7 w-7 flex items-center justify-center border border-[#E7E5E4] rounded-full hover:bg-[#F5F5F4] transition-colors'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M10 7.5C10 6.83696 10.2634 6.20107 10.7322 5.73223C11.2011 5.26339 11.837 5 12.5 5C13.163 5 13.7989 5.26339 14.2678 5.73223C14.7366 6.20107 15 6.83696 15 7.5V12.5C15 13.163 14.7366 13.7989 14.2678 14.2678C13.7989 14.7366 13.163 15 12.5 15C11.837 15 11.2011 14.7366 10.7322 14.2678C10.2634 13.7989 10 13.163 10 12.5V7.5Z'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                )}
              </div>

              <div className='flex items-center gap-3'>
                {showMicrophone && (
                  <button className='h-7 w-7 flex items-center justify-center border border-[#E7E5E4] rounded-full hover:bg-[#F5F5F4] transition-colors'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M10 12.5C10.663 12.5 11.2989 12.2366 11.7678 11.7678C12.2366 11.2989 12.5 10.663 12.5 10V5C12.5 4.33696 12.2366 3.70107 11.7678 3.23223C11.2989 2.76339 10.663 2.5 10 2.5C9.33696 2.5 8.70107 2.76339 8.23223 3.23223C7.76339 3.70107 7.5 4.33696 7.5 5V10C7.5 10.663 7.76339 11.2989 8.23223 11.7678C8.70107 12.2366 9.33696 12.5 10 12.5Z'
                        fill='currentColor'
                      />
                      <path
                        d='M15 10C15 10 15 12.5 10 12.5C5 12.5 5 10 5 10M10 12.5V17.5M7.5 17.5H12.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={!inputValue.trim()}
                  className='h-8 w-8 flex items-center justify-center bg-[#FDE047] rounded-sm hover:bg-[#FDE047]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10 15V5M10 5L5 10M10 5L15 10'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Disclaimer */}
            {disclaimerText && (
              <p className='text-xs text-[#1C1917] text-center px-2'>
                {disclaimerText}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';
